import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import QuizComponent from '../../components/QuizComponent';
import { Compass, BookOpen, Search, Layers, AlertTriangle, Zap } from 'lucide-react';

const hermeneuticsContent = {
  principles: [
    {
      id: 'new-covenant-lens',
      title: 'The New Covenant Lens',
      icon: Zap,
      content: `The most important hermeneutical principle for Christians: read everything through the lens of the New Covenant and Christ's finished work.

"In speaking of a new covenant, he makes the first one obsolete. And what is becoming obsolete and growing old is ready to vanish away." (Hebrews 8:13 ESV)

The Old Covenant—including the Law of Moses and Ten Commandments as a rule of life—is OBSOLETE for believers. This doesn't mean we throw out the Old Testament. It means we interpret it through Christ's fulfillment.

"For Christ is the end of the law for righteousness to everyone who believes." (Romans 10:4 ESV)

When reading Old Testament commands, ask: "Does the New Testament apply this to believers, or has Christ fulfilled it?" When encountering promises, ask: "How does this find its 'yes' in Christ?" (2 Corinthians 1:20)

**Key Principles:**
• The Old Covenant is obsolete; the New Covenant is operative (Hebrews 8:13)
• Believers are not under law but under grace (Romans 6:14)
• We are led by the Spirit, not by written code (Romans 7:6; Galatians 5:18)
• All Scripture is profitable, but not all Scripture directly applies to New Covenant believers`
    },
    {
      id: 'predestination-corporate',
      title: 'Predestination: God\'s Corporate Plan',
      icon: Search,
      content: `Much confusion arises from misunderstanding Ephesians 1 and Romans 9-11. These passages reveal God's plan to include GENTILES in His people—not individual pre-selection for heaven or hell.

"In him we have obtained an inheritance, having been predestined according to the purpose of him who works all things according to the counsel of his will, so that we who were the first to hope in Christ might be to the praise of his glory. In him you also, when you heard the word of truth, the gospel of your salvation, and believed in him, were sealed with the promised Holy Spirit." (Ephesians 1:11-13 ESV)

Notice the context: Paul is explaining how BOTH Jews ("we who were the first to hope") AND Gentiles ("you also") are now included in God's plan. This was "the mystery" hidden for ages—that Gentiles would be fellow heirs (Ephesians 3:6).

Romans 9-11 isn't about individual salvation but about Israel's rejection and the Gentiles' inclusion. Paul asks: Has God rejected His people? No—but branches were broken off so Gentiles could be grafted in.

**Corporate, not individual:**
• "Chosen IN HIM" (Ephesians 1:4) – Christ is the Chosen One; we are chosen by being in Him
• "The mystery" = Gentiles included with Jews (Ephesians 3:4-6)
• Romans 9-11 = Israel/Gentile question, not individual election`
    },
    {
      id: 'scripture-interprets',
      title: 'Scripture Interprets Scripture',
      icon: BookOpen,
      content: `Clear passages interpret unclear passages. This is especially important when encountering "scary verses" that seem to contradict grace.

When you encounter a passage that seems to teach:
• Loss of salvation → Anchor it to John 10:28-29, Romans 8:38-39, Hebrews 10:14
• Need for repeated cleansing → Anchor it to Colossians 2:13-14, Hebrews 10:10-14
• Conditional forgiveness → Anchor it to Ephesians 1:7, Colossians 2:13

The clear teaching of the New Testament is: believers are totally forgiven, permanently united, and eternally secure. Any interpretation that contradicts this must be re-examined.

Example: 1 John 1:9 ("If we confess our sins...") seems to teach Christians need repeated cleansing. But Hebrews 10:14 says we're "perfected for all time" and Colossians 2:13-14 says "ALL trespasses forgiven." So 1 John 1:9 must be addressing unbelievers (context: Gnostics denying sin), not describing Christian maintenance.

**Anchor verses for interpreting "scary passages":**
• Total forgiveness: Ephesians 1:7; Colossians 2:13-14; Hebrews 10:14
• Permanent union: 1 Corinthians 6:17
• Eternal security: John 10:28-29; Romans 8:38-39`
    },
    {
      id: 'context-is-king',
      title: 'Context Determines Meaning',
      icon: Layers,
      content: `Every verse exists within concentric circles of context: immediate passage, chapter, book, testament, whole Bible. Ripping verses out of context inevitably distorts meaning.

**Critical questions for context:**
• Who is speaking? (Jesus under Old Covenant? Paul to Gentiles? John to Gnostics?)
• Who is the audience? (Jews? Gentiles? Believers? Unbelievers?)
• When was it written? (Pre-cross? Post-cross? During transition?)
• What is the surrounding argument?

**Example: The Lord's Prayer (Matthew 6:12)**
"Forgive us our debts, as we also have forgiven our debtors."

Context: Jesus taught this BEFORE the cross, to Jews under Old Covenant. The disciples did not yet have access to Christ's finished work. After the cross, believers HAVE forgiveness (Ephesians 1:7)—we don't request what we already possess.

**Example: Hebrews Warnings (chapters 6, 10)**
Context: Written to JEWS considering abandoning Christianity to return to temple sacrifices. The warnings urge: "Don't reject the once-for-all sacrifice!" These aren't about Christians losing salvation but about the impossibility of a second sacrifice if Christ's was rejected.`
    },
    {
      id: 'avoiding-errors',
      title: 'Common Errors to Avoid',
      icon: AlertTriangle,
      content: `Several interpretive errors undermine grace. Watch for these:

**1. Mixing Covenants**
Applying Old Covenant commands directly to New Covenant believers. We are not under law (Romans 6:14), not under the Ten Commandments as a rule of life (2 Corinthians 3:7-11), but led by the Spirit (Galatians 5:18).

**2. Assuming All Bible = Direct Application**
All Scripture is profitable (2 Timothy 3:16), but not all Scripture directly applies to you. The dietary laws, Sabbath commands, and sacrificial system were shadows fulfilled in Christ (Colossians 2:16-17).

**3. Fear-Based Interpretation**
If an interpretation makes you afraid, insecure, or doubt God's love—question the interpretation, not God's character. He is not a harsh master but a loving Father.

**4. Performance-Based Reading**
Reading every passage as "what I must do" rather than "what Christ has done." The gospel is announcement, not advice. News, not instructions.

**5. "Two Natures" Framework**
Reading believers as having an "old nature" and "new nature" at war. Scripture says the old has PASSED AWAY (2 Corinthians 5:17). You have ONE nature—new.

**The cure: Always anchor interpretation to the finished work. Does your reading align with "It is finished"?**`
    }
  ]
};

export default function HermeneuticsHub() {
  return (
    <div className="min-h-[calc(100vh-64px)]" data-testid="hermeneutics-hub">
      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1695730866912-fdbbf93e2250?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Open book representing study and interpretation"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hub-hero-overlay flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 w-full">
            <span className="inline-block px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm mb-3">
              Step 3 · Understanding
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              Hermeneutics
            </h1>
            <p className="text-white/80 mt-2 max-w-2xl">
              Reading Scripture through the New Covenant lens
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="principles" className="space-y-8">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="principles" data-testid="tab-principles">Principles</TabsTrigger>
            <TabsTrigger value="quiz" data-testid="tab-quiz">Quiz</TabsTrigger>
          </TabsList>

          <TabsContent value="principles" className="space-y-8">
            {/* Introduction */}
            <div className="max-w-3xl">
              <p className="text-lg text-muted-foreground leading-relaxed">
                How you read the Bible determines what you see. Many Christians read Scripture through 
                Old Covenant glasses—seeing commands to keep, conditions to meet, fellowship to maintain. 
                But New Covenant reading sees Christ's finished work as the lens through which EVERYTHING is interpreted.
              </p>
            </div>

            {/* Key Verse */}
            <Card className="max-w-3xl bg-primary/5 border-primary/20">
              <CardContent className="p-6">
                <p className="font-serif text-xl italic mb-2">
                  "In speaking of a new covenant, he makes the first one obsolete. And what is 
                  becoming obsolete and growing old is ready to vanish away."
                </p>
                <p className="text-primary font-medium">— Hebrews 8:13 ESV</p>
              </CardContent>
            </Card>

            {/* Principles Accordion */}
            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="space-y-4">
                {hermeneuticsContent.principles.map((principle, index) => {
                  const Icon = principle.icon;
                  return (
                    <AccordionItem 
                      key={principle.id} 
                      value={principle.id}
                      className="border rounded-lg px-6"
                    >
                      <AccordionTrigger className="hover:no-underline py-4">
                        <div className="flex items-center gap-3 text-left">
                          <span className="flex-shrink-0 p-2 rounded-lg bg-primary/10">
                            <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                          </span>
                          <span className="font-serif text-lg font-semibold">{principle.title}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-6">
                        <div className="pl-14 prose prose-slate max-w-none">
                          {principle.content.split('\n\n').map((paragraph, pIndex) => (
                            <p key={pIndex} className="text-foreground/80 leading-relaxed mb-4 last:mb-0 whitespace-pre-wrap">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>

            {/* Summary */}
            <Card className="max-w-3xl mx-auto">
              <CardHeader>
                <CardTitle className="font-serif">The New Covenant Reading Method</CardTitle>
                <CardDescription>Always ask these questions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="font-semibold mb-1">1. Which Covenant?</p>
                    <p className="text-sm text-muted-foreground">Is this Old or New Covenant context?</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="font-semibold mb-1">2. Pre or Post-Cross?</p>
                    <p className="text-sm text-muted-foreground">Was this before or after "It is finished"?</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="font-semibold mb-1">3. Who Is Addressed?</p>
                    <p className="text-sm text-muted-foreground">Believers? Unbelievers? Jews? Gentiles?</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="font-semibold mb-1">4. Does It Align?</p>
                    <p className="text-sm text-muted-foreground">Does this reading align with the finished work?</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quiz">
            <div className="max-w-2xl mx-auto">
              <QuizComponent hubId="hermeneutics" />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
